<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        Programas y Fechas Proyectadas
        <v-spacer></v-spacer>
        <v-tooltip bottom v-if="$store.state.CARGO.id === 1">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon small class="mr-1"
              v-bind="attrs" v-on="on"
              :loading="recalculandoCron"
              :disabled="recalculandoCron"
              @click="recalcularCron"
            >
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Recalcular estados (cron manual)</span>
        </v-tooltip>
        <v-btn icon @click="showFilters = !showFilters">
          <v-icon>{{ showFilters ? 'mdi-chevron-up' : 'mdi-filter' }}</v-icon>
        </v-btn>
      </v-card-title>

      <v-expand-transition>
        <div v-show="showFilters">
          <v-card-text>
            <!-- Fila 1: identificadores y sede/estado -->
            <v-row dense>
              <v-col cols="12" md="2">
                <v-text-field v-model="filters.programa_id" label="ID(s)" placeholder="ej: 8352, 8353" @input="debouncedFetch" clearable dense outlined hide-details></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field v-model="filters.nombre" label="Nombre" @input="debouncedFetch" clearable dense outlined hide-details></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field v-model="filters.codigo" label="Código" @input="debouncedFetch" clearable dense outlined hide-details></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-autocomplete v-model="filters.sede_id" :items="sedes" item-text="nombre" item-value="id" label="Sede" @change="fetchProgramas" clearable dense outlined hide-details></v-autocomplete>
              </v-col>
              <v-col cols="12" md="2">
                <v-autocomplete v-model="filters.estado_programa_codigo" :items="estadosPrograma" label="Estado" @change="fetchProgramas" clearable dense outlined hide-details></v-autocomplete>
              </v-col>
              <v-col cols="12" md="2">
                <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dateRangeText"
                      label="Fecha Comercialización"
                      prepend-inner-icon="mdi-calendar"
                      readonly v-bind="attrs" v-on="on"
                      dense outlined hide-details
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="filters.dates" range @input="dateMenu = false; fetchProgramas()"></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <!-- Fila 2: filtros adicionales -->
            <v-row dense class="mt-1">
              <v-col cols="12" md="2">
                <v-autocomplete v-model="filters.categoria" :items="categorias" item-text="text" item-value="value" label="Categoría" @change="fetchProgramas" clearable dense outlined hide-details></v-autocomplete>
              </v-col>
              <v-col cols="12" md="2">
                <v-autocomplete v-model="filters.modalidad" :items="modalidades" item-text="text" item-value="value" label="Modalidad" @change="fetchProgramas" clearable dense outlined hide-details></v-autocomplete>
              </v-col>
              <v-col cols="12" md="1">
                <v-text-field v-model.number="filters.gestion" label="Gestión" type="number" placeholder="2025" @input="debouncedFetch" clearable dense outlined hide-details></v-text-field>
              </v-col>
              <v-col cols="12" md="1">
                <v-select v-model="filters.fase" :items="fases" item-text="text" item-value="value" label="Fase" @change="fetchProgramas" clearable dense outlined hide-details></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-select v-model="filters.estado" :items="estadosActivo" item-text="text" item-value="value" label="Activo" @change="fetchProgramas" dense outlined hide-details></v-select>
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-center justify-end">
                <v-btn color="primary" @click="fetchProgramas" :loading="loading" title="Buscar / Actualizar" class="mr-1">
                  <v-icon>mdi-magnify</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <!-- Fila 3: toggles -->
            <v-row dense class="mt-1">
              <v-col cols="12" class="d-flex align-center flex-wrap" style="gap:8px">
                <v-chip
                  :color="filters.en_riesgo ? 'red darken-2' : 'grey lighten-1'"
                  :dark="filters.en_riesgo"
                  label class="font-weight-bold" style="cursor:pointer"
                  @click="toggleEnRiesgo"
                >
                  <v-icon left small>mdi-alert-circle</v-icon>
                  En riesgo{{ filters.en_riesgo ? ` (${totalProgramas})` : '' }}
                </v-chip>
                <v-text-field
                  v-if="filters.en_riesgo"
                  v-model.number="filters.dias_anticipacion"
                  type="number" min="1" max="30"
                  label="Días de anticipación" dense outlined hide-details
                  style="max-width:160px"
                  @input="debouncedFetch"
                />
                <v-chip
                  :color="filters.es_estrella ? 'amber darken-2' : 'grey lighten-1'"
                  :dark="filters.es_estrella"
                  label class="font-weight-bold" style="cursor:pointer"
                  @click="filters.es_estrella = !filters.es_estrella; fetchProgramas()"
                >
                  <v-icon left small>mdi-star</v-icon>
                  Estrella
                </v-chip>
                <v-chip
                  :color="filters.inicio_excepcional_modulo0 ? 'deep-purple darken-1' : 'grey lighten-1'"
                  :dark="filters.inicio_excepcional_modulo0"
                  label class="font-weight-bold" style="cursor:pointer"
                  @click="filters.inicio_excepcional_modulo0 = !filters.inicio_excepcional_modulo0; fetchProgramas()"
                >
                  <v-icon left small>mdi-rocket-launch-outline</v-icon>
                  Inicio excepcional
                </v-chip>

                <!-- Brochure -->
                <v-chip
                  :color="filters.brochure_subido === true ? 'teal darken-1' : filters.brochure_subido === false ? 'orange darken-2' : 'grey lighten-1'"
                  :dark="filters.brochure_subido != null"
                  label class="font-weight-bold" style="cursor:pointer"
                  @click="ciclarFiltroBooleano('brochure_subido')"
                >
                  <v-icon left small>mdi-file-image-outline</v-icon>
                  Brochure{{ filters.brochure_subido === true ? ': Con' : filters.brochure_subido === false ? ': Sin' : '' }}
                </v-chip>

                <!-- Config. Clases -->
                <v-chip
                  :color="filters.configuracion_clases_completa === true ? 'teal darken-1' : filters.configuracion_clases_completa === false ? 'orange darken-2' : 'grey lighten-1'"
                  :dark="filters.configuracion_clases_completa != null"
                  label class="font-weight-bold" style="cursor:pointer"
                  @click="ciclarFiltroBooleano('configuracion_clases_completa')"
                >
                  <v-icon left small>mdi-cog-outline</v-icon>
                  Config. Clases{{ filters.configuracion_clases_completa === true ? ': Completa' : filters.configuracion_clases_completa === false ? ': Incompleta' : '' }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>
        </div>
      </v-expand-transition>

      <v-alert
        v-if="filters.en_riesgo"
        type="warning" dense outlined class="mx-4 mb-2" icon="mdi-alert-circle"
      >
        Mostrando <strong>{{ totalProgramas }}</strong> programas que cambiarán de estado en los próximos
        <strong>{{ filters.dias_anticipacion }}</strong> días sin haber alcanzado el punto de equilibrio (25 inscritos efectivos).
        <v-btn x-small text color="warning" class="ml-2" @click="toggleEnRiesgo">Limpiar filtro</v-btn>
      </v-alert>

      <v-data-table
        :headers="headersActivos"
        :items="programas"
        :options.sync="options"
        :server-items-length="totalProgramas"
        :loading="loading"
        class="elevation-1"
      >
        <template v-slot:item.id="{ item }">
          <v-chip small class="font-weight-bold" title="ID de Programa">
            {{ item.categoria ? item.categoria.charAt(0) : '' }}-{{ item.id }}
          </v-chip>
        </template>

        <template v-slot:item.codigo="{ item }">
          <v-chip small title="Código de Programa">{{ item.codigo }}</v-chip>
        </template>

        <template v-slot:item.programa_universidad_codigo="{ item }">
          <v-chip v-if="item.programa_universidad_codigo" x-small>{{ item.programa_universidad_codigo }}</v-chip>
          <span v-else>-</span>
        </template>

        <template v-slot:item.nombre_compuesto="{ item }">
          <span class="text-caption">{{ item.nombre_compuesto }}</span>
        </template>

        <template v-slot:item.categoria="{ item }">
          <v-chip small>{{ item.categoria }}</v-chip>
          <v-icon small class="ml-1" :title="getModalidadTitle(item.modalidad)">
            {{ getModalidadIcon(item.modalidad) }}
          </v-icon>
        </template>

        <template v-slot:item.estado_programa_codigo="{ item }">
          <v-chip small :color="getColorForEstado(item.estado_programa_codigo)" dark>
            {{ item.estado_programa_codigo }}
          </v-chip>
        </template>
        <template v-slot:item.etapa_nombre="{ item }">
          <span class="text-caption">{{ item.etapa_nombre }}</span>
        </template>

        <template v-slot:item.progreso="{ item }">
          <span>{{ item.modulo_actual_numero || '-' }} / {{ item.cantidad_modulos }}</span>
        </template>

        <template v-slot:item.inscritos="{ item }">
          <div class="d-flex flex-column" style="gap:2px; min-width:80px">
            <div class="d-flex align-center" style="gap:4px">
              <v-icon x-small color="green darken-2">mdi-account-check</v-icon>
              <span class="caption font-weight-bold" :class="item.inscritos_efectivos >= 25 ? 'green--text text--darken-2' : item.inscritos_efectivos >= 13 ? 'orange--text text--darken-2' : ''">
                {{ item.inscritos_efectivos }}
              </span>
              <span class="caption grey--text">ef.</span>
            </div>
            <div class="d-flex align-center" style="gap:4px">
              <v-icon x-small color="blue darken-1">mdi-account-clock</v-icon>
              <span class="caption">{{ item.preinscritos }}</span>
              <span class="caption grey--text">preinsc.</span>
            </div>
            <div class="d-flex align-center" style="gap:4px">
              <v-icon x-small color="grey">mdi-account-outline</v-icon>
              <span class="caption">{{ item.prospectos }}</span>
              <span class="caption grey--text">prosp.</span>
            </div>
          </div>
        </template>

        <template v-slot:item.proximo_estado="{ item }">
          <div v-if="item.estado_siguiente_planificado_codigo || item.estado_siguiente_requisitos_codigo">
            <div class="d-flex align-center mb-1" style="gap:4px" v-if="item.estado_siguiente_planificado_codigo">
              <v-icon x-small color="grey darken-1">mdi-calendar-clock</v-icon>
              <v-chip x-small :color="getColorForEstado(item.estado_siguiente_planificado_codigo)" dark>
                {{ traducirEstado(item.estado_siguiente_planificado_codigo) }}
              </v-chip>
            </div>
            <div class="d-flex align-center" style="gap:4px" v-if="item.estado_siguiente_requisitos_codigo">
              <v-icon
                x-small
                :color="item.estado_siguiente_requisitos_codigo !== item.estado_siguiente_planificado_codigo ? 'green darken-2' : 'grey darken-1'"
              >mdi-check-circle</v-icon>
              <v-chip
                x-small
                :color="getColorForEstado(item.estado_siguiente_requisitos_codigo)"
                dark
                :outlined="item.estado_siguiente_requisitos_codigo === item.estado_siguiente_planificado_codigo"
              >
                {{ traducirEstado(item.estado_siguiente_requisitos_codigo) }}
              </v-chip>
            </div>
          </div>
          <span v-else class="caption grey--text">—</span>
        </template>

        <template v-slot:item.fechas_programa="{ item }">
          <div class="caption" style="white-space:nowrap">
            <div v-if="item.fecha_inicio"><v-icon x-small color="blue darken-1">mdi-play-circle-outline</v-icon> {{ item.fecha_inicio }}</div>
            <div v-if="item.fecha_fin"><v-icon x-small color="red darken-1">mdi-stop-circle-outline</v-icon> {{ item.fecha_fin }}</div>
          </div>
        </template>

        <template v-slot:item.proxima_fecha="{ item }">
            <div v-if="proximaFecha(item)">
                <div class="font-weight-medium">{{ proximaFecha(item).fecha_proyectada }}</div>
                <div class="text-caption">{{ traducirTipoFecha(proximaFecha(item).tipo_fecha) }}</div>
                <div class="text-caption">(en {{ diasRestantes(proximaFecha(item).fecha_proyectada) }} días)</div>
            </div>
            <span v-else>-</span>
        </template>

        <template v-slot:item.dias_para_proximo_cambio="{ item }">
          <v-chip
            v-if="item.dias_para_proximo_cambio != null"
            small
            :color="item.dias_para_proximo_cambio <= 1 ? 'red darken-2' : item.dias_para_proximo_cambio <= 3 ? 'orange darken-2' : 'yellow darken-3'"
            dark
          >
            <v-icon left x-small>mdi-clock-alert-outline</v-icon>
            {{ item.dias_para_proximo_cambio }}d
          </v-chip>
          <span v-else class="caption grey--text">—</span>
        </template>

        <template v-slot:item.brochure_subido="{ item }">
          <v-icon v-if="item.brochure_subido" color="green darken-1" small title="Brochure subido">mdi-check-circle</v-icon>
          <v-icon v-else color="grey lighten-1" small title="Sin brochure">mdi-circle-outline</v-icon>
        </template>

        <template v-slot:item.configuracion_clases_completa="{ item }">
          <v-icon v-if="item.configuracion_clases_completa" color="green darken-1" small title="Configuración completa">mdi-check-circle</v-icon>
          <v-icon v-else color="grey lighten-1" small title="Sin configuración">mdi-circle-outline</v-icon>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon x-small v-bind="attrs" v-on="on" @click="showDetalle(item)">
                <v-icon small>mdi-timeline-text-outline</v-icon>
              </v-btn>
            </template>
            <span>Ver fechas proyectadas</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon x-small
                v-bind="attrs" v-on="on"
                @click="$router.push({ name: 'programa-configuracion-clases', params: { id: item.id } })"
              >
                <v-icon small color="blue darken-2">mdi-calendar-clock</v-icon>
              </v-btn>
            </template>
            <span>Configuración de clases</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon x-small v-bind="attrs" v-on="on" @click="showSolicitud(item)">
                <v-icon small color="orange darken-2">mdi-calendar-edit</v-icon>
              </v-btn>
            </template>
            <span>Solicitudes cambio fecha matrícula</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon x-small v-bind="attrs" v-on="on" @click="showAnticipo(item)">
                <v-icon small color="deep-purple darken-2">mdi-rocket-launch-outline</v-icon>
              </v-btn>
            </template>
            <span>Solicitudes anticipo de módulo</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialogDetalle" max-width="800px">
        <FechasProyectadasDetalle
            v-if="selectedProgramaId"
            :programa-id="selectedProgramaId"
            @close="dialogDetalle = false"
            @recalculated="fetchProgramas"
        />
    </v-dialog>

    <v-dialog v-model="dialogSolicitud" max-width="680px">
      <SolicitudFechaMatricula
        v-if="selectedSolicitudPrograma"
        :key="selectedSolicitudPrograma.id"
        :programa-id="selectedSolicitudPrograma.id"
        :nombre-programa="selectedSolicitudPrograma.nombre_compuesto"
        :fecha-matricula="selectedSolicitudPrograma.fecha_matriculacion"
        @close="dialogSolicitud = false"
        @aprobada="fetchProgramas"
      />
    </v-dialog>

    <v-dialog v-model="dialogAnticipo" max-width="680px">
      <SolicitudAnticipoModulo
        v-if="selectedAnticipoPrograma"
        :key="selectedAnticipoPrograma.id"
        :programa-id="selectedAnticipoPrograma.id"
        :programa-nombre="selectedAnticipoPrograma.nombre_compuesto"
      />
    </v-dialog>

  </v-container>
</template>

<script>
import xaxios from '@/xaxios';
import _ from 'lodash';
import gql from 'graphql-tag';
import FechasProyectadasDetalle from './FechasProyectadasDetalle.vue';
import SolicitudFechaMatricula from './SolicitudFechaMatricula.vue';
import SolicitudAnticipoModulo from './SolicitudAnticipoModulo.vue';

const API_URL = `${process.env.VUE_APP_API_REST}/api/v1`;

export default {
  name: 'FechasProyectadasListado',
  components: { FechasProyectadasDetalle, SolicitudFechaMatricula, SolicitudAnticipoModulo },
  data() {
    return {
      programas: [],
      loading: true,
      totalProgramas: 0,
      options: { sortDesc: [true] },
      showFilters: true,
      dateMenu: false,
      filters: {
        programa_id: null,
        nombre: null,
        codigo: null,
        sede_id: null,
        estado_programa_codigo: null,
        categoria: null,
        modalidad: null,
        gestion: null,
        fase: null,
        estado: null,
        es_estrella: false,
        inicio_excepcional_modulo0: false,
        brochure_subido: null,
        configuracion_clases_completa: null,
        dates: [],
        en_riesgo: false,
        dias_anticipacion: 5,
      },
      sedes: [],
      estadosPrograma: [],
      categorias: [
        { value: 'DIPLOMADO', text: 'Diplomado' },
        { value: 'ESPECIALIDAD', text: 'Especialidad' },
        { value: 'MAESTRIA', text: 'Maestría' },
      ],
      modalidades: [
        { value: 'V', text: 'Virtual' },
        { value: 'P', text: 'Presencial' },
        { value: 'S', text: 'Semipresencial' },
      ],
      estadosActivo: [
        { value: null, text: 'Todos' },
        { value: true, text: 'Activos' },
        { value: false, text: 'Inactivos' },
      ],
      fases: [
        { value: 0, text: 'Fase 0' },
        { value: 1, text: 'Fase 1' },
        { value: 2, text: 'Fase 2' },
        { value: 3, text: 'Fase 3' },
        { value: 4, text: 'Fase 4' },
      ],
      headers: [
        { text: 'ID', value: 'id', sortable: true, width: '80px' },
        { text: 'Cód. Programa', value: 'codigo', sortable: true, width: '130px' },
        { text: 'Cód. Académico', value: 'programa_universidad_codigo', sortable: false, width: '130px' },
        { text: 'Nombre del Programa', value: 'nombre_compuesto', sortable: true },
        { text: 'Sede', value: 'sede_nombre', sortable: true, width: '110px' },
        { text: 'Categoría', value: 'categoria', sortable: false, width: '100px' },
        { text: 'Fecha Comercialización', value: 'fecha_matriculacion', sortable: true, width: '120px' },
        { text: 'Inicio / Fin', value: 'fechas_programa', sortable: false, width: '110px' },
        { text: 'Etapa', value: 'etapa_nombre', sortable: false, width: '110px' },
        { text: 'Estado', value: 'estado_programa_codigo', sortable: true, width: '120px' },
        { text: 'Próx. Estado', value: 'proximo_estado', sortable: false, width: '160px' },
        { text: 'Módulos', value: 'progreso', sortable: false, width: '80px' },
        { text: 'Inscritos', value: 'inscritos', sortable: false, width: '110px' },
        { text: 'Próxima Fecha Crítica', value: 'proxima_fecha', sortable: false, width: '160px' },
        { text: 'Brochure', value: 'brochure_subido', sortable: false, width: '80px' },
        { text: 'Config. Clases', value: 'configuracion_clases_completa', sortable: false, width: '100px' },
        { text: 'Acciones', value: 'actions', sortable: false, width: '100px' },
      ],
      dialogDetalle: false,
      selectedProgramaId: null,
      dialogSolicitud: false,
      selectedSolicitudPrograma: null,
      dialogAnticipo: false,
      selectedAnticipoPrograma: null,
      recalculandoCron: false,
    };
  },
  watch: {
    options: {
      handler() {
        this.fetchProgramas();
      },
      deep: true,
    },
    '$store.state.CARGO'(newCargo) {
      this.filters.sede_id = newCargo?.sede?.id || null;
      this.fetchSedes();
    },
  },
  computed: {
    dateRangeText() {
      return this.filters.dates.join(' ~ ');
    },
    headersActivos() {
      if (this.filters.en_riesgo) {
        const idx = this.headers.findIndex(h => h.value === 'proximo_estado');
        return [
          ...this.headers.slice(0, idx + 1),
          { text: 'Días restantes', value: 'dias_para_proximo_cambio', sortable: true, width: '110px' },
          ...this.headers.slice(idx + 1),
        ];
      }
      return this.headers;
    },
  },
  created() {
    this.debouncedFetch = _.debounce(this.fetchProgramas, 500);
    const cargo = this.$store.state.CARGO;
    if (cargo?.sede?.id) this.filters.sede_id = cargo.sede.id;
    this.fetchSedes();
    this.fetchEstados();
  },
  methods: {
    fetchSedes() {
      const cargos = this.$store.state.CARGOS;
      if (cargos && cargos.length) {
        const sedesMap = new Map();
        cargos.forEach(c => {
          if (c.sede && c.sede.id) sedesMap.set(c.sede.id, { id: c.sede.id, nombre: c.sede.nombre });
        });
        this.sedes = [...sedesMap.values()];
      } else {
        const sql = gql`query { getSedes { id nombre } }`;
        this.$apollo
          .query({ query: sql })
          .then(({ data: { getSedes } }) => { this.sedes = getSedes; })
          .catch((e) => window.console.error('Error cargando sedes:', e));
      }
    },
    async fetchEstados() {
      try {
        const { data } = await xaxios.get(`${API_URL}/programa/estados`);
        const raw = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        this.estadosPrograma = raw.map(e => (typeof e === 'string' ? e : e.codigo)).filter(Boolean);
      } catch (e) {
        window.console.error('Error cargando estados:', e);
      }
    },
    async fetchProgramas() {
      this.loading = true;
      const { sortDesc, page, itemsPerPage } = this.options;

      const cargos = this.$store.state.CARGOS || [];
      const sedesPermitidas = cargos.length
        ? [...new Set(cargos.map(c => c.sede?.id).filter(Boolean))].join(',')
        : undefined;

      const params = {
        page,
        per_page: itemsPerPage,
        order: sortDesc && sortDesc[0] ? 'DESC' : 'ASC',
        sedes_ids: sedesPermitidas || undefined,
        sede_id: this.filters.sede_id || undefined,
        programa_id: this.filters.programa_id || undefined,
        nombre: this.filters.nombre || undefined,
        codigo: this.filters.codigo || undefined,
        estado_programa_codigo: this.filters.estado_programa_codigo || undefined,
        categoria: this.filters.categoria || undefined,
        modalidad: this.filters.modalidad || undefined,
        gestion: this.filters.gestion || undefined,
        fase: this.filters.fase != null ? this.filters.fase : undefined,
        estado: this.filters.estado != null ? this.filters.estado : undefined,
        es_estrella: this.filters.es_estrella || undefined,
        inicio_excepcional_modulo0: this.filters.inicio_excepcional_modulo0 || undefined,
        brochure_subido: this.filters.brochure_subido != null ? this.filters.brochure_subido : undefined,
        configuracion_clases_completa: this.filters.configuracion_clases_completa != null ? this.filters.configuracion_clases_completa : undefined,
        fecha_matriculacion_desde: this.filters.dates[0] || undefined,
        fecha_matriculacion_hasta: this.filters.dates[1] || undefined,
      };

      if (this.filters.en_riesgo) {
        params.en_riesgo = true;
        params.dias_anticipacion = this.filters.dias_anticipacion || 5;
        params.order = 'ASC';
      }

      try {
        const { data } = await xaxios.get(`${API_URL}/programas`, { params });
        this.programas = data.data;
        this.totalProgramas = data.total;
      } catch (error) {
        window.console.error("Error fetching programas:", error);
        this.$root.$notify.show('Error al cargar los programas.', { color: 'error' });
      } finally {
        this.loading = false;
      }
    },
    async recalcularCron() {
      this.recalculandoCron = true;
      try {
        const { data } = await xaxios.post(`${API_URL}/programa-estado-cron/recalcular`);
        const r = data.data || {};
        const msg = `Recálculo completado — Actualizados: ${r.actualizados ?? '?'}, Sin cambios: ${r.sin_cambios ?? '?'}, Errores: ${r.errores ?? '?'}`;
        this.$root.$notify.show(msg, { color: 'success' });
        await this.fetchProgramas();
      } catch (e) {
        const msg = e?.response?.data?.error || 'Error al ejecutar el recálculo de estados';
        this.$root.$notify.show(msg, { color: 'error' });
      } finally {
        this.recalculandoCron = false;
      }
    },
    toggleEnRiesgo() {
      this.filters.en_riesgo = !this.filters.en_riesgo;
      if (this.filters.en_riesgo) {
        this.filters.programa_id = null;
        this.filters.nombre = null;
        this.filters.codigo = null;
        this.filters.sede_id = this.$store.state.CARGO?.sede?.id || null;
        this.filters.estado_programa_codigo = null;
        this.filters.categoria = null;
        this.filters.modalidad = null;
        this.filters.gestion = null;
        this.filters.fase = null;
        this.filters.estado = null;
        this.filters.es_estrella = false;
        this.filters.inicio_excepcional_modulo0 = false;
        this.filters.dates = [];
      }
      this.fetchProgramas();
    },
    ciclarFiltroBooleano(campo) {
      const actual = this.filters[campo];
      if (actual === null) this.filters[campo] = true;
      else if (actual === true) this.filters[campo] = false;
      else this.filters[campo] = null;
      this.fetchProgramas();
    },
    showDetalle(item) {
        this.selectedProgramaId = item.id;
        this.dialogDetalle = true;
    },
    showSolicitud(item) {
      this.selectedSolicitudPrograma = item;
      this.dialogSolicitud = true;
    },
    showAnticipo(item) {
      this.selectedAnticipoPrograma = item;
      this.dialogAnticipo = true;
    },
    getModalidadIcon(modalidad) {
        const icons = { V: 'mdi-laptop', P: 'mdi-account-group', S: 'mdi-lan-connect' };
        return icons[modalidad] || 'mdi-help-circle';
    },
    getModalidadTitle(modalidad) {
        const titles = { V: 'Virtual', P: 'Presencial', S: 'Semipresencial' };
        return titles[modalidad] || 'Desconocido';
    },
    getColorForEstado(codigo) {
      const colores = {
        PROXIMO: 'grey',
        CAMPANIA: 'green darken-1',
        ARRASTRE: 'yellow darken-3',
        ARRASTRE_CRITICO: 'orange darken-2',
        COMERCIALIZACION_EXTENDIDA: 'teal darken-1',
        DESARROLLO_CON_ADMISION: 'cyan darken-2',
        INICIADO_MODULO_0: 'blue',
        DESARROLLO: 'blue darken-3',
        EN_DESARROLLO_SIN_PE: 'blue darken-2',
        CULMINADO: 'purple',
        DADO_DE_BAJA: 'red darken-1',
        BAJA_ARRASTRE_CRITICO: 'red darken-2',
      };
      return colores[codigo] || 'grey';
    },

    traducirEstado(codigo) {
      const map = {
        PROXIMO: 'Próximo',
        CAMPANIA: 'En campaña',
        ARRASTRE: 'En arrastre',
        ARRASTRE_CRITICO: 'Arr. crítico',
        COMERCIALIZACION_EXTENDIDA: 'Com. extendida',
        DESARROLLO_CON_ADMISION: 'Des. c/admisión',
        INICIADO_MODULO_0: 'Inicio Mód. 0',
        DESARROLLO: 'Desarrollo',
        EN_DESARROLLO_SIN_PE: 'Des. sin P.E.',
        CULMINADO: 'Culminado',
        DADO_DE_BAJA: 'Dado de baja',
        BAJA_ARRASTRE_CRITICO: 'Baja arr. crítico',
      };
      return map[codigo] || codigo;
    },
    traducirTipoFecha(tipo) {
      const map = {
        FIN_CAMPANIA: 'Fin de campaña',
        INICIO_ARRASTRE: 'Inicio arrastre',
        FIN_ARRASTRE: 'Fin arrastre',
        INICIO_ARRASTRE_CRITICO: 'Inicio arrastre crítico',
        FIN_ARRASTRE_CRITICO: 'Fin arrastre crítico',
        INICIO_MODULO_0: 'Inicio Módulo 0',
      };
      return map[tipo] || tipo;
    },
    proximaFecha(item) {
        if (!item.fechas_proyectadas || item.fechas_proyectadas.length === 0) return null;
        const hoy = new Date().toISOString().slice(0, 10);
        const futuras = item.fechas_proyectadas
            .filter((f) => f.fecha_proyectada >= hoy)
            .sort((a, b) => a.fecha_proyectada.localeCompare(b.fecha_proyectada));
        return futuras[0] || null;
    },
    diasRestantes(fechaISO) {
        if (!fechaISO) return 0;
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        const target = new Date(fechaISO);
        target.setHours(0, 0, 0, 0);
        return Math.ceil((target.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
    }
  },
};
</script>

<style scoped>
.v-data-table {
    font-size: 0.875rem;
}
.text-caption {
    font-size: 0.75rem !important;
    color: grey;
}
</style>